import Axios from "axios";
import {
  SET_ADDRESS_INFO_LIST,
  UNSET_ADDRESS_INFO_LIST,
  SET_ADDRESS_INFO_DETAILS,
  UNSET_ADDRESS_INFO_DETAILS,
} from '@/store/mutation-types';
import {
  getAddressInfoList,
  getAddressInfoById,
  getAddressInfoByEmployee,
  saveUpdateAddressInfo,
  saveEmployeeAddressInfo,
  updateAddressInfo,
  deleteAddressInfo,
} from '@/store/api';

const state = {
  addressInfoList: [],
  currentAddressInfo: null,
  loading: false,
  error: null
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  [SET_ADDRESS_INFO_LIST](state, list) {
    state.addressInfoList = list
  },
  [SET_ADDRESS_INFO_DETAILS](state, info) {
    state.currentAddressInfo = info
  },
  [UNSET_ADDRESS_INFO_LIST](state) {
    state.addressInfoList = []
  },
  [UNSET_ADDRESS_INFO_DETAILS](state) {
    state.currentAddressInfo = null
  },
  ADD_ADDRESS_INFO(state, info) {
    state.addressInfoList.push(info)
  },
  UPDATE_ADDRESS_INFO(state, updatedInfo) {
    const index = state.addressInfoList.findIndex(item => item.id === updatedInfo.id)
    if (index !== -1) {
      state.addressInfoList.splice(index, 1, updatedInfo)
    }
  },
  REMOVE_ADDRESS_INFO(state, id) {
    state.addressInfoList = state.addressInfoList.filter(item => item.id !== id)
  }
}

const actions = {
  async fetchAddressInfoList({ commit }, params = {}) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      const response = await Axios.post(getAddressInfoList, params)
      if (response.data.success) {
        commit('SET_ADDRESS_INFO_LIST', response.data.data || [])
      } else {
        commit('SET_ERROR', response.data.message)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAddressInfoById({ commit }, id) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      const response = await Axios.get(getAddressInfoById(id))
      if (response.data.success) {
        commit('SET_ADDRESS_INFO_DETAILS', response.data.data)
      } else {
        commit('SET_ERROR', response.data.message)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAddressInfoByEmployee({ commit }, employeeId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      const response = await Axios.get(getAddressInfoByEmployee(employeeId))
      if (response.data.success) {
        commit('SET_ADDRESS_INFO_DETAILS', response.data.data)
      } else {
        commit('SET_ERROR', response.data.message)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async saveAddressInfo({ commit }, addressData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      const response = await Axios.post(saveUpdateAddressInfo, addressData)
      if (response.data.success) {
        if (addressData.id) {
          commit('UPDATE_ADDRESS_INFO', response.data.data)
        } else {
          commit('ADD_ADDRESS_INFO', response.data.data)
        }
      } else {
        commit('SET_ERROR', response.data.message)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async saveEmployeeAddressInfo({ commit }, { employeeId, addressData }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      const response = await Axios.post(saveEmployeeAddressInfo, {
        employee_id: employeeId,
        address_data: addressData
      })
      if (response.data.success) {
        commit('SET_ADDRESS_INFO_DETAILS', response.data.data)
      } else {
        commit('SET_ERROR', response.data.message)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateAddressInfo({ commit }, { id, addressData }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      const response = await Axios.put(updateAddressInfo(id), addressData)
      if (response.data.success) {
        commit('UPDATE_ADDRESS_INFO', response.data.data)
      } else {
        commit('SET_ERROR', response.data.message)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteAddressInfo({ commit }, id) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      const response = await Axios.delete(deleteAddressInfo(id))
      if (response.data.success) {
        commit('REMOVE_ADDRESS_INFO', id)
      } else {
        commit('SET_ERROR', response.data.message)
      }
      return response.data
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const getters = {
  addressInfoList: state => state.addressInfoList,
  currentAddressInfo: state => state.currentAddressInfo,
  loading: state => state.loading,
  error: state => state.error
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
